import React, { useEffect, useState } from 'react';
import CardOrderToday from './CardOrderToday';
import { useNavigate } from 'react-router-dom';
import { useStoreHook } from '../../Hooks/StoreHook';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { useCartContext } from '../../Contexts/CartContext';
import Scrollbars from 'react-custom-scrollbars-2';
import BoxContentEndScrollbars from './BoxContentEndScrollbars';
import { ImSpinner8 } from 'react-icons/im';
import { TbMoodConfuzed } from 'react-icons/tb';
import ButtonPrimary from '../Form/ButtonPrimary';
import { isOrderOpen } from '../../Utils/Helpers';
import { useTranslation } from 'react-i18next';
import { useOrderHook } from '../../Hooks/OrderHook';
import { useCheckoutContext } from '../../Contexts/CheckoutContext';
import ButtonCustomerOrders from './ButtonCustomerOrders';
import Button from '../Form/Button';
import { useLoginContext } from '../../Contexts/LoginContext';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';

type Order = {
    id: string;
    status: number;
};

const OrdersToday = () => {

    const { t } = useTranslation();
    const { getLinkToHome } = useStoreHook();
    const { getOrdersToday, handleGetDetail } = useOrderHook();
    const { setShowOrderDetail } = useCartContext();
    const { isAuthenticated, isAuthenticatedToQuickPurchase, user } = useLoginContext();
    const { setShowTodayOrders, setShowSearchBar } = useLayoutContext();
    const { isStoreCounter, orderType, isMenu } = useOrderTypeContext();
    const { ordersTodayList, setAwaitingOrPendingCount } = useCheckoutContext();
    const [ ordersTodayListOpened, setOrdersTodayListOpened ] = useState<Order[]>([]);
    const [ ordersTodayListOthers, setOrdersTodayListOthers ] = useState<Order[]>([]);
    const [ hasOrdersToday, setHasOrdersToday ] = useState(false);
    const [ loadingOrdersToday ] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setOrdersTodayListOpened(ordersTodayList.filter((order) => isOrderOpen(order)))
        setOrdersTodayListOthers(ordersTodayList.filter((order) => !isOrderOpen(order)))
        setHasOrdersToday(ordersTodayList.length > 0 && !loadingOrdersToday);
    }, [ordersTodayList, loadingOrdersToday]);

    const handleBackToHome = () => {
        navigate(getLinkToHome());
        setShowOrderDetail(false);
        setShowTodayOrders(false);
    }

    const getOrdersTodayPolling = async () => {
        if (isStoreCounter || !user?.id || (!isAuthenticated && !isAuthenticatedToQuickPurchase) || orderType == null) {
            setAwaitingOrPendingCount(0);
            return;
        }
        if (!isMenu) {
            getOrdersToday();
        }
    };

    useEffect(() => {
        getOrdersTodayPolling();

        const interval = setInterval(() => {
            getOrdersTodayPolling();
        }, 15000);

        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, isAuthenticated, isAuthenticatedToQuickPurchase, orderType]);

    return (
        <div className="flex flex-col justify-between h-full">
            <div className='relative h-full'>

                <Scrollbars autoHide={true} className='min-h-full'>
                    <div className="flex flex-col min-h-full justify-between h-full gap-4">
                        {loadingOrdersToday &&
                            <div className="flex items-center justify-center gap-1 min-h-full text-gray-500"><ImSpinner8 className="animate-spin" /><span>{t('orders_today.loading_orders')}</span></div>
                        }

                        {ordersTodayList.length === 0 && !loadingOrdersToday &&
                            <section className='flex flex-col w-full h-full items-center justify-center text-center'>
                                <TbMoodConfuzed size={50} className="mb-3 animate-fade-in" />
                                <div className='text-xl font-bold'>{t('orders_today.no_orders_title')}</div>
                                <p className='leading-5 px-2'>{t('orders_today.no_orders_message')}</p>

                                <ButtonPrimary
                                    onClick={handleBackToHome}
                                    className="mt-6">
                                    {t('orders_today.button_start')}
                                </ButtonPrimary>
                            </section>
                        }

                        {hasOrdersToday &&
                            <div className='flex flex-col gap-10 p-4'>

                                {ordersTodayListOpened.length > 0 &&
                                    <div className='flex flex-col gap-3'>
                                        <div className='flex items-center gap-2 justify-center'>
                                            <hr className='border-gray-100 w-5' />
                                            <span className='text-nowrap font-bold text-base text-shopfood-title'>{t('orders_today.open_orders_title')}</span>
                                            <hr className='border-gray-100 w-full' />
                                        </div>
                                        {ordersTodayListOpened.map((order: any) => <CardOrderToday order={order} key={order.id} onClick={() => handleGetDetail(order)} />)}
                                    </div>
                                }

                                {ordersTodayListOthers.length > 0 &&
                                    <div className='flex flex-col gap-3 opacity-75'>
                                        <div className='flex items-center gap-2 justify-center'>
                                            <hr className='border-gray-100 w-5' />
                                            <span className='text-nowrap font-semibold text-base text-shopfood-title'>{t('orders_today.completed_orders_title')}</span>
                                            <hr className='border-gray-100 w-full' />
                                        </div>
                                        {ordersTodayListOthers.map((order: any) =>
                                            <CardOrderToday order={order} key={order.id} onClick={() => handleGetDetail(order)} />
                                        )}
                                    </div>
                                }

                                <BoxContentEndScrollbars message={t('orders_today.end_of_list')} />

                            </div>
                        }

                        {!loadingOrdersToday &&
                            <div className="flex items-center gap-2 justify-between flex-wrap p-4 md:pb-2 sticky bottom-0 bg-white/65 backdrop-blur-sm">
                                <ButtonPrimary outline onClick={handleBackToHome} className='text-nowrap flex-1 ring-inset'>
                                    {t('orders_today.button_make_order')}
                                </ButtonPrimary>

                                <ButtonCustomerOrders className='flex flex-1 w-full'>
                                    <Button onClick={() => setShowSearchBar(false)} disabled={!isAuthenticated} className='w-full text-nowrap'>
                                        {t('menu.main.orders.label')}
                                    </Button>
                                </ButtonCustomerOrders>
                            </div>
                        }
                    </div>
                </Scrollbars>

            </div>

        </div>
    )
}

export default OrdersToday;